import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectProps } from '@mui/material/Select';
import { typedMemo } from '@utils';
import { useMemo } from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { BaseControllerProps, SelectOption } from './types';
import { useFormFieldValidation } from './useFormFieldValidation';

interface SelectFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<SelectProps, 'name' | 'value' | 'onChange' | 'error'>,
    BaseControllerProps<TFieldValues, TName> {
  options: SelectOption[];
  placeholder?: string;
  formControlProps?: { fullWidth?: boolean; disabled?: boolean };
}

const SelectFormField = typedMemo(
  <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  >({
    name,
    control,
    rules,
    options,
    placeholder,
    label,
    required = false,
    formControlProps = { fullWidth: true, disabled: false },
    ...selectProps
  }: Readonly<SelectFormFieldProps<TFieldValues, TName>>) => {
    const validationRules = useFormFieldValidation<TFieldValues, TName>(
      required,
      rules,
    );
    const labelId = useMemo(() => `${String(name)}-label`, [name]);

    const menuItems = useMemo(() => {
      const items = [];

      if (placeholder) {
        items.push(
          <MenuItem key="placeholder" value="" disabled>
            {placeholder}
          </MenuItem>,
        );
      }

      items.push(
        ...options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        )),
      );

      return items;
    }, [placeholder, options]);

    return (
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={({ field, fieldState: { error } }) => (
          <FormControl
            fullWidth={formControlProps.fullWidth}
            error={!!error}
            disabled={formControlProps.disabled}
          >
            {label && (
              <InputLabel id={labelId} required={required}>
                {label}
              </InputLabel>
            )}
            <Select
              {...field}
              {...selectProps}
              labelId={labelId}
              label={label}
              displayEmpty={!!placeholder}
            >
              {menuItems}
            </Select>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    );
  },
);
